/**
 * Strategy interfaces and shared helpers used by all strategy implementations.
 */
import { Decimal } from 'decimal.js';
import { IMAGE_URLS, PACKAGE_IDS } from '../utils/constants.js';
/**
 * Struct tag for the ALPHA coin used by the distributor.
 */
export const ALPHA_COIN_TYPE = 'fe3afec26c59e874f3c1d60b8203cb3852d2bb2aa415df9548b8d688e6683f93::alpha::ALPHA';
/**
 * Base class shared by all strategies.
 * Provides parsing helpers and common reward calculation logic.
 */
export class BaseStrategy {
    createAlphaFiReceipt(tx) {
        return tx.moveCall({
            target: `${PACKAGE_IDS.ALPHAFI_RECEIPT}::alphafi_receipt::create_alphafi_receipt_v2`,
            arguments: [tx.pure.string(IMAGE_URLS.ALPHAFI_RECEIPT)],
        });
    }
    /**
     * Compute claimable ALPHA rewards using the distributor + pool/receipt accounting.
     * Returns a human-readable amount (divided by 1e9).
     */
    getAlphaMiningRewardsToClaim(distributor) {
        const data = this.getAlphaMiningData();
        // No receipt -> no position -> no alpha rewards.
        if (!data.receipt) {
            return new Decimal(0);
        }
        const { poolId, accRewardsPerXtoken, xTokenSupply, receipt } = data;
        const { lastAccRewardPerXtoken, pendingRewards, xTokenBalance } = receipt;
        // Get user's last accumulated reward per xtoken for ALPHA
        const userLastAccEntry = lastAccRewardPerXtoken.find((entry) => entry.key === ALPHA_COIN_TYPE);
        const userLastAcc = new Decimal(userLastAccEntry?.value || '0');
        // Get user's xtoken balance
        const userXtokenBalance = new Decimal(xTokenBalance || '0');
        // Get pending alpha rewards from receipt
        const pendingAlphaRewards = pendingRewards
            .filter((entry) => entry.key === ALPHA_COIN_TYPE)
            .reduce((acc, entry) => acc.add(new Decimal(entry.value || '0')), new Decimal(0));
        // Get total alpha weight across all pools from distributor
        const alphaRewardTotalWeight = distributor.poolAllocator.totalWeights.find((w) => w.key === ALPHA_COIN_TYPE)?.value || '0';
        // Get distribution status for this pool
        const memberEntry = distributor.poolAllocator.members.find((m) => m.key === poolId);
        const alphaAllocatorDetailsForPool = memberEntry?.value.poolData.find((pd) => pd.key === ALPHA_COIN_TYPE)?.value;
        // Calculate pending rewards for pool
        let pendingRewardsForPool = new Decimal(0);
        if (alphaAllocatorDetailsForPool) {
            const currentTime = Date.now();
            const pendingRewardsInAllocator = new Decimal(alphaAllocatorDetailsForPool.pendingRewards || '0');
            const lastUpdateTime = parseInt(alphaAllocatorDetailsForPool.lastUpdateTime || '0', 10);
            const weight = new Decimal(alphaAllocatorDetailsForPool.weight || '0');
            const totalWeight = new Decimal(alphaRewardTotalWeight);
            const target = new Decimal(distributor.target || '0');
            // Calculate unlockPerMS (target per millisecond)
            // unlock_per_ms = target * 2750000 / (4250000 * 100 * 86400000)
            const unlockPerMs = target
                .mul(new Decimal('2750000'))
                .div(new Decimal('4250000').mul('100').mul('86400000'));
            // Calculate time diff
            const timeDiff = Math.max(0, currentTime - lastUpdateTime);
            // Calculate additional rewards
            let additionalRewards = new Decimal(0);
            if (totalWeight.gt(0)) {
                additionalRewards = new Decimal(timeDiff).mul(unlockPerMs).mul(weight).div(totalWeight);
            }
            pendingRewardsForPool = pendingRewardsInAllocator.add(additionalRewards);
        }
        // Get pool's accumulated rewards per xtoken for ALPHA
        const alphaOldAccEntry = accRewardsPerXtoken.find((entry) => entry.key === ALPHA_COIN_TYPE);
        const alphaOldAccInPool = new Decimal(alphaOldAccEntry?.value || '0');
        // Get total xtoken supply from pool
        const totalXtokenSupply = new Decimal(xTokenSupply || '0');
        // Calculate new accumulated rewards per xtoken
        const SCALE = new Decimal('1000000000000000000'); // 1e18
        let addAcc = new Decimal(0);
        if (totalXtokenSupply.gt(0)) {
            addAcc = pendingRewardsForPool.div(totalXtokenSupply).mul(SCALE).mul(SCALE);
        }
        const alphaNewAccInPool = alphaOldAccInPool.add(addAcc);
        // Calculate user's accrued rewards
        // user_accrued_rewards = ((acc_new - acc_last) / SCALE^2) * user_balance
        const deltaAcc = alphaNewAccInPool.sub(userLastAcc);
        const userAccruedRewards = deltaAcc.div(SCALE.mul(SCALE)).mul(userXtokenBalance);
        // Combine pending rewards and accrued rewards, then divide by 1e9 for human-readable amount
        const totalPendingRewards = pendingAlphaRewards.add(userAccruedRewards).div(new Decimal(1e9));
        return totalPendingRewards;
    }
    // ===== Parsing Helper Methods =====
    /**
     * Helper function to get string field from JSON with validation
     */
    getStringField(fieldsJson, fieldName) {
        const value = fieldsJson?.[fieldName];
        if (typeof value === 'string') {
            return value;
        }
        if (value !== undefined && value !== null) {
            return String(value);
        }
        return '';
    }
    /**
     * Helper function to get boolean field from JSON with default value
     */
    getBooleanField(fieldsJson, fieldName, defaultValue) {
        const value = fieldsJson?.[fieldName];
        if (typeof value === 'boolean') {
            return value;
        }
        return defaultValue;
    }
    /**
     * Helper function to get number field from JSON
     */
    getNumberField(fieldsJson, fieldName) {
        const value = fieldsJson?.[fieldName];
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            const parsed = parseInt(value, 10);
            return isNaN(parsed) ? 0 : parsed;
        }
        return 0;
    }
    /**
     * Helper function to parse VecMap structures from Move objects into StringMap format
     */
    parseVecMap(vecMapField) {
        if (!vecMapField || typeof vecMapField !== 'object') {
            return [];
        }
        const contents = vecMapField.contents;
        if (!Array.isArray(contents)) {
            return [];
        }
        return contents.map((entry) => {
            const rawKey = entry?.key;
            const key = typeof rawKey === 'string' ? rawKey : rawKey?.name;
            const value = entry?.value;
            return { key, value };
        });
    }
    /**
     * Helper function to convert ASCII array from JSON to string
     */
    asciiToString(asciiArray) {
        if (Array.isArray(asciiArray)) {
            try {
                const bytes = asciiArray.map((n) => {
                    const num = typeof n === 'number' ? n : parseInt(n);
                    if (num < 0 || num > 255) {
                        throw new Error('ASCII value out of range');
                    }
                    return num;
                });
                return String.fromCharCode(...bytes);
            }
            catch (error) {
                console.error('Error converting ASCII array to string:', error);
                return '';
            }
        }
        return '';
    }
    /**
     * Helper function to extract fields from various response formats
     */
    extractFields(response) {
        return response.fields || response.content?.fields || response;
    }
    /**
     * Helper function to safely parse nested object fields
     */
    getNestedField(obj, path) {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    }
    /**
     * Helper function to parse object with error handling
     */
    safeParseObject(parseFunction, errorMessage) {
        try {
            return parseFunction();
        }
        catch (error) {
            console.error(`${errorMessage}:`, error);
            throw new Error(`${errorMessage}: ${error}`);
        }
    }
}
//# sourceMappingURL=strategy.js.map